import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import './DataTable.css';

export default function EventRegistrants() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [event, setEvent]   = useState(null);
  const [rows,  setRows]    = useState([]);
  const [total, setTotal]   = useState(0);
  const [page,  setPage]    = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 20;

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/events/${id}/registrants`, { params: { page, limit, search } })
      .then(r => {
        setEvent(r.data.event);
        setRows(r.data.registrants);
        setTotal(r.data.pagination?.total || 0);
      })
      .catch(() => showToast('Failed to load registrants', 'error'))
      .finally(() => setLoading(false));
  }, [id, page, search, showToast]);

  const totalPages = Math.ceil(total / limit);

  return (
    <Layout>
      <div className="data-table-page">
        <div className="page-header">
          <div>
            <h1>Event Registrants</h1>
            {event && <p>Registrants for <strong>{event.title}</strong></p>}
          </div>
          <button className="btn btn-outline" onClick={() => navigate('/admin/events')}>← Back to Events</button>
        </div>

        {event && (
          <div className="event-info-bar">
            <span className="info-pill">
              📅 {event.sessions_count || 0} session{event.sessions_count !== 1 ? 's' : ''}
            </span>
            <span className="info-pill">
              👥 {total} registrant{total !== 1 ? 's' : ''}
            </span>
            <span className={`info-pill ${event.is_free ? 'free' : 'paid'}`}>
              {event.is_free ? '🎉 Free' : `💳 ${event.currency} ${parseFloat(event.price || 0).toFixed(2)}`}
            </span>
          </div>
        )}

        <div className="toolbar">
          <div className="search-box">
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input
              placeholder="Search by name, email or phone…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="table-loading"><div className="spinner" /></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p>No registrants found{search ? ' matching your search' : ''}.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Session</th>
                  <th>Status</th>
                  <th>Registered At</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id}>
                    <td className="text-muted">{(page - 1) * limit + i + 1}</td>
                    <td>
                      <div className="name-cell">
                        <div className="avatar-sm">{r.firstname?.[0]}{r.lastname?.[0]}</div>
                        <span>{r.firstname} {r.lastname}</span>
                      </div>
                    </td>
                    <td>{r.email}</td>
                    <td>{r.country_code} {r.phone}</td>
                    <td>
                      {r.session_label || (r.session_date
                        ? new Date(r.session_date).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })
                        : '—')}
                    </td>
                    <td>
                      <span className={`badge badge-${r.payment_status === 'success' || r.is_free ? 'success' : r.payment_status === 'pending' ? 'warning' : 'danger'}`}>
                        {r.is_free ? 'Free' : r.payment_status || 'pending'}
                      </span>
                    </td>
                    <td className="text-muted">{new Date(r.registered_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          </div>
        )}
      </div>
    </Layout>
  );
}
